import axios from 'axios';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../icon/components/global/header/Header';
import Footer from '../icon/components/global/footer/Footer';
import { api } from '../Api';

/**
 * Public download page for the Android app (we host the APK ourselves — it is
 * not on the Play Store). No sign-in: an old build that is refused at login
 * must still be able to fetch the update from here.
 */

const fmtSize = (n) => {
  const b = Number(n) || 0;
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${(b / 1024).toFixed(0)} KB`;
  return b ? `${b} B` : '';
};

const STEPS = [
  {
    en: 'Tap "Download for Android" and wait for the download to finish.',
    bn: '"Download for Android" বাটনে চাপ দিন এবং ডাউনলোড শেষ হওয়া পর্যন্ত অপেক্ষা করুন।',
  },
  {
    en: 'Open the downloaded file (IconMarketing-….apk) from the notification or your Downloads folder.',
    bn: 'নোটিফিকেশন বা Downloads ফোল্ডার থেকে ডাউনলোড হওয়া ফাইলটি (IconMarketing-….apk) খুলুন।',
  },
  {
    en: 'If Android asks, allow "Install unknown apps" for your browser (Settings → Allow from this source), then go back.',
    bn: 'ফোন অনুমতি চাইলে আপনার ব্রাউজারের জন্য "Install unknown apps" চালু করুন (Settings → Allow from this source), তারপর ফিরে আসুন।',
  },
  {
    en: 'Tap Install (or Update). When it finishes, open the app and sign in.',
    bn: 'Install (বা Update) চাপুন। শেষ হলে অ্যাপটি খুলে লগইন করুন।',
  },
];

export default function DownloadAppScreen() {
  const [state, setState] = useState({ loading: true, release: null, error: '' });

  useEffect(() => {
    let alive = true;
    axios
      .get(`${api}/api/app/releases/latest`, { params: { platform: 'android' } })
      .then(({ data }) => alive && setState({ loading: false, release: data?.release || null, error: '' }))
      .catch(() => alive && setState({ loading: false, release: null, error: 'Could not load the app details. Please try again.' }));
    return () => { alive = false; };
  }, []);

  const { loading, release, error } = state;
  const notes = String(release?.notes || '').split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  const href = `${api}/api/app/releases/download/latest?platform=android`;

  return (
    <>
      <Header />
      <Helmet><title>Download the Icon Marketing app</title></Helmet>
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '28px 16px 60px' }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 22, textAlign: 'center' }}>
          <h1 style={{ fontSize: 26, margin: '0 0 4px' }}>Icon Marketing</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>Android app · অ্যান্ড্রয়েড অ্যাপ</p>

          {loading ? (
            <p style={{ marginTop: 18, color: '#6b7280' }}>Loading…</p>
          ) : release ? (
            <>
              <p style={{ margin: '14px 0 0', fontSize: 14, color: '#374151' }}>
                Version <strong>{release.versionName}</strong>
                {fmtSize(release.size) ? <> · {fmtSize(release.size)}</> : null}
                {release.publishedAt ? <> · {new Date(release.publishedAt).toLocaleDateString()}</> : null}
              </p>
              <a
                href={href}
                style={{ display: 'inline-block', marginTop: 18, background: '#16a34a', color: '#fff', textDecoration: 'none', borderRadius: 12, padding: '14px 28px', fontWeight: 700, fontSize: 18 }}
              >
                Download for Android
              </a>
              <p style={{ margin: '8px 0 0', fontSize: 12, color: '#9ca3af' }}>অ্যান্ড্রয়েডের জন্য ডাউনলোড করুন</p>
            </>
          ) : (
            <p style={{ marginTop: 18, color: '#b45309' }}>
              {error || 'The app is not available for download yet. · অ্যাপটি এখনও ডাউনলোডের জন্য প্রস্তুত নয়।'}
            </p>
          )}
        </div>

        {notes.length ? (
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 18, marginTop: 14 }}>
            <h2 style={{ fontSize: 17, margin: '0 0 8px' }}>What is new · নতুন যা আছে</h2>
            <ul style={{ margin: 0, paddingLeft: 20, color: '#374151' }}>
              {notes.map((n, i) => <li key={i} style={{ marginBottom: 4 }}>{n}</li>)}
            </ul>
          </div>
        ) : null}

        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 16, padding: 18, marginTop: 14 }}>
          <h2 style={{ fontSize: 17, margin: '0 0 10px' }}>How to install · কীভাবে ইনস্টল করবেন</h2>
          <ol style={{ margin: 0, paddingLeft: 20 }}>
            {STEPS.map((s, i) => (
              <li key={i} style={{ marginBottom: 10 }}>
                <span style={{ display: 'block', color: '#111827' }}>{s.en}</span>
                <span style={{ display: 'block', color: '#4b5563', fontSize: 14 }}>{s.bn}</span>
              </li>
            ))}
          </ol>
          <p style={{ margin: '8px 0 0', fontSize: 12, color: '#6b7280' }}>
            Only download the app from this page. · শুধুমাত্র এই পেজ থেকেই অ্যাপটি ডাউনলোড করুন।
          </p>
        </div>
      </div>
      <Footer />
    </>
  );
}
